import { motion } from 'framer-motion';

interface TambolaBallProps {
  number: number;
  color: 'gold' | 'blue' | 'purple' | 'magenta';
  size?: number; // size in pixels
  className?: string;
  delay?: number;
  duration?: number;
  yOffsetRange?: number;
}

/**
 * A glossy 3D-styled Tambola number ball built using pure CSS gradients and Framer Motion.
 */
export function TambolaBall({
  number,
  color,
  size = 64,
  className = '',
  delay = 0,
  duration = 4,
  yOffsetRange = 15,
}: TambolaBallProps) {
  // Define color palettes with 3D radial highlights
  const colorGradients = {
    gold: {
      ball: 'radial-gradient(circle at 30% 30%, #ffe082 0%, #ffb300 50%, #b78103 100%)',
      glow: 'rgba(255, 179, 0, 0.4)',
      border: 'border-amber-400/30',
    },
    blue: {
      ball: 'radial-gradient(circle at 30% 30%, #90caf9 0%, #1e88e5 50%, #0d47a1 100%)',
      glow: 'rgba(30, 136, 229, 0.4)',
      border: 'border-blue-400/30',
    },
    purple: {
      ball: 'radial-gradient(circle at 30% 30%, #d1c4e9 0%, #5e35b1 50%, #311b92 100%)',
      glow: 'rgba(94, 53, 177, 0.4)',
      border: 'border-purple-400/30',
    },
    magenta: {
      ball: 'radial-gradient(circle at 30% 30%, #f8bbd0 0%, #d81b60 50%, #880e4f 100%)',
      glow: 'rgba(216, 27, 96, 0.4)',
      border: 'border-pink-400/30',
    },
  };

  const selectedPalette = colorGradients[color];

  return (
    <motion.div
      initial={{ y: 0, opacity: 0, scale: 0.8 }}
      animate={{
        y: [-yOffsetRange, yOffsetRange, -yOffsetRange],
        opacity: 1,
        scale: 1,
      }}
      transition={{
        y: {
          duration,
          repeat: Infinity,
          ease: 'easeInOut',
        },
        opacity: { duration: 1, delay },
        scale: { duration: 0.8, delay },
      }}
      style={{
        width: size,
        height: size,
        background: selectedPalette.ball,
        boxShadow: `0 8px 24px rgba(0,0,0,0.4), inset 0 -4px 12px rgba(0,0,0,0.6), 0 0 20px ${selectedPalette.glow}`,
      }}
      className={`relative flex items-center justify-center rounded-full border ${selectedPalette.border} cursor-default select-none ${className}`}
    >
      {/* Inner white circle containing the number */}
      <div
        style={{
          width: size * 0.6,
          height: size * 0.6,
          background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)',
        }}
        className="flex items-center justify-center rounded-full"
      >
        <span
          style={{ fontSize: size * 0.28 }}
          className="font-display font-black text-slate-800 tracking-tight"
        >
          {number}
        </span>
      </div>

      {/* Surface glossy reflection streak */}
      <div
        style={{
          width: size * 0.25,
          height: size * 0.12,
          top: size * 0.1,
          left: size * 0.2,
          transform: 'rotate(-15deg)',
        }}
        className="absolute rounded-full bg-white/40 blur-[1px]"
      />
    </motion.div>
  );
}
